import { IBaseRequestParams, IRequestParams, type Headers } from '../models/types';
import { HttpMethod } from '../models/enums';
import { RestApiError, StatusUtils } from '../utils';
import { captureException, setContext, setUser } from '@sentry/react';
import { LocalStorageUtils } from '@shared/utils';
import { useLoginStore, useTokenStore } from '@lib/store';

const IGNORED_STATUS_ERRORS = [401];

export class Api {
  private static readonly baseURL = process.env.API_URL;

  protected static get<Response>(request: IRequestParams<null>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request({
        method: HttpMethod.Get,
        url: request.url,
        signal: request.signal,
        responseType: request.responseType,
      }),
    );
  }

  protected static post<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethod.Post,
        url: request.url,
        data: request.data,
        signal: request.signal,
      }),
    );
  }

  protected static put<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethod.Put,
        url: request.url,
        data: request.data,
        signal: request.signal,
      }),
    );
  }

  protected static delete<Request, Response>(request: IRequestParams<Request>): Promise<Response> {
    return this._wrapRequest(() =>
      this._request<Request, Response>({
        method: HttpMethod.Delete,
        url: request.url,
        data: request.data,
        signal: request.signal,
      }),
    );
  }

  private static async _wrapRequest<Response>(handler: () => Promise<Response>) {
    try {
      const result = await handler();
      return result;
    } catch (error) {
      if (RestApiError.isRestApiError(error) && IGNORED_STATUS_ERRORS.includes(error.status)) error.message = '';
      throw error;
    }
  }

  private static async _request<Request, Response>(request: IBaseRequestParams<Request>): Promise<Response> {
    const requestId = crypto.randomUUID();
    const headers =
      request.data && request.data instanceof FormData
        ? { ...(request.headers || {}) }
        : ({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Request-Id': requestId,
            ...(request.headers || {}),
          } as Headers);
    const authUser = await LocalStorageUtils.getStuff();

    if (!headers.Authorization && headers.Authorization !== '' && authUser != null) {
      const accessToken = authUser.token;
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    }

    const options = {
      method: request.method || 'GET',
      body: request.data && request.data instanceof FormData ? request.data : request.data ? JSON.stringify(request.data) : null,
      headers,
      signal: request.signal,
      responseType: request.responseType,
    };

    const response = await fetch(this.baseURL + request.url, options);

    if (response.status === 401 && authUser.refreshToken) {
      LocalStorageUtils.removeStuff();
      useLoginStore.getState().clear();
      useTokenStore.getState().clear();
    }

    if (response.ok) {
      const contentType = response.headers.get('content-type');

      if (response.status === 204) return response.status as Response;
      if (contentType && contentType.indexOf('application/json') !== -1) return response.json();
      return {
        data: request.responseType === 'blob' ? await response.blob() : await response.arrayBuffer(),
        contentType: contentType,
        contentDisposition: response.headers.get('content-disposition'),
      } as Response;
    } else {
      const errorMessage = StatusUtils.getErrorMessage(response);
      const error = new RestApiError(errorMessage || 'Unknown error', response.status, request.method, request.url);

      if (!IGNORED_STATUS_ERRORS.includes(response.status)) {
        setContext('Request', {
          requestId,
          method: request.method,
          url: request.url,
          status: response.status,
          message: errorMessage || 'Unknown error',
        });
        setUser({ id: 'tusr' });
        captureException(error);
      }
      setUser({ id: authUser?.profile.sid });
      captureException(error);

      throw error;
    }
  }
}
